import React, { useState } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { useApi } from '../../../api/useApi';

type PlatformRole = 'operator' | 'admin';
type InviteMode = 'google' | 'password';

interface AdminInviteFormProps {
  brand: BrandConfig;
}

// Shape of POST /api/admin/users/invitations success body (SOR-35). Only the
// fields this form reads are typed; the endpoint returns more (expiresAt,
// createdAt, setupLink/inviteLink, emailStatus).
interface InvitationResponse {
  success: boolean;
  invitation?: {
    id: string;
    email: string;
    platformRole: PlatformRole;
    mode: InviteMode;
    status: string;
  };
  emailStatus?: string;
}

// Permissive-but-present client-side check. The backend is the canonical
// validator (`z.string().email()`); this only blocks an obviously-empty/invalid
// address so the form gives immediate feedback without diverging from the server.
const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const platformRoles: { value: PlatformRole; label: string }[] = [
  { value: 'operator', label: 'Operator' },
  { value: 'admin', label: 'Admin' },
];

const inviteModes: { value: InviteMode; label: string; hint: string }[] = [
  { value: 'google', label: 'Google', hint: 'Invitee signs in with Google (SSO)' },
  { value: 'password', label: 'Password', hint: 'Invitee receives a setup link to create a password' },
];

const AdminInviteForm: React.FC<AdminInviteFormProps> = ({ brand }) => {
  const { post } = useApi();
  const [email, setEmail] = useState('');
  const [platformRole, setPlatformRole] = useState<PlatformRole>('operator');
  const [mode, setMode] = useState<InviteMode>('google');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      const result = await post<InvitationResponse>('/api/admin/users/invitations', {
        email: email.trim(),
        platformRole,
        mode,
      });
      const role = result.invitation?.platformRole ?? platformRole;
      setSuccess(`Invitation sent to ${email.trim()} as ${role}.`);
      setEmail('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-6" style={{ backgroundColor: brand.colors.bgLight }}>
      <div className="max-w-xl">
        <h1 className="text-2xl font-semibold mb-1" style={{ color: brand.colors.secondary }}>
          Invite a team member
        </h1>
        <p className="text-sm mb-6" style={{ color: brand.colors.textLight }}>
          Send an invitation to an operator or admin. Choose how they will sign in.
        </p>

        {/* noValidate: this form runs its OWN validation (`isValidEmail`) and
            renders a custom `invite-error` banner. Without it, the browser's
            native type="email" constraint validation preempts the submit event
            for an invalid address, so `handleSubmit` — and the custom error UX —
            never runs. */}
        <form onSubmit={handleSubmit} noValidate data-testid="admin-invite-form" className="space-y-6">
          {/* Email */}
          <div>
            <label
              htmlFor="invite-email"
              className="block text-sm font-medium mb-1.5"
              style={{ color: brand.colors.text }}
            >
              Email address
            </label>
            <input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="person@example.com"
              data-testid="invite-email"
              className="w-full px-3 py-2 rounded-lg border text-sm"
              style={{ borderColor: brand.colors.border, color: brand.colors.text }}
            />
          </div>

          {/* Platform role */}
          <div>
            <label
              htmlFor="invite-platform-role"
              className="block text-sm font-medium mb-1.5"
              style={{ color: brand.colors.text }}
            >
              Platform role
            </label>
            <select
              id="invite-platform-role"
              value={platformRole}
              onChange={(e) => setPlatformRole(e.target.value as PlatformRole)}
              data-testid="invite-platform-role"
              className="w-full px-3 py-2 rounded-lg border text-sm"
              style={{ borderColor: brand.colors.border, color: brand.colors.text }}
            >
              {platformRoles.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          {/* Sign-in mode toggle */}
          <div>
            <span className="block text-sm font-medium mb-1.5" style={{ color: brand.colors.text }}>
              Sign-in method
            </span>
            <div
              className="inline-flex rounded-lg border p-1 gap-1"
              role="group"
              aria-label="Sign-in method"
              style={{ borderColor: brand.colors.border }}
            >
              {inviteModes.map((m) => {
                const isActive = mode === m.value;
                return (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setMode(m.value)}
                    aria-pressed={isActive}
                    data-testid={`invite-mode-${m.value}`}
                    className="px-4 py-1.5 text-sm font-medium rounded-md transition-colors"
                    style={{
                      backgroundColor: isActive ? brand.colors.primary : 'transparent',
                      color: isActive ? brand.colors.onPrimary : brand.colors.textLight,
                    }}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs mt-1.5" style={{ color: brand.colors.textLight }}>
              {inviteModes.find((m) => m.value === mode)?.hint}
            </p>
          </div>

          {/* Submit */}
          <div>
            <button
              type="submit"
              disabled={loading}
              data-testid="invite-submit"
              className="px-6 py-2.5 text-sm font-medium rounded-full transition-all duration-200 disabled:opacity-50"
              style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
            >
              {loading ? 'Sending…' : 'Send invitation'}
            </button>
          </div>

          {/* Result states */}
          {success && (
            <div
              className="p-4 rounded-lg text-sm"
              data-testid="invite-success"
              role="status"
              style={{ backgroundColor: brand.colors.primaryLight, color: brand.colors.secondary }}
            >
              {success}
            </div>
          )}
          {error && (
            <div
              className="p-4 rounded-lg text-sm bg-red-50 text-red-800"
              data-testid="invite-error"
              role="alert"
            >
              {error}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default AdminInviteForm;
